import { useSnackbar } from "notistack";
import { createLeadTask } from "@/services/leads.service";
import { plainTextToHtml } from "@/utils/strings";
import { getErrorFromBackend } from "@/utils/errors";
import { useFollowUpPromptStore } from "@/store/tasks/followUpPrompt.store";
import { useTasksRefreshStore } from "@/store/tasks/tasksRefresh.store";
import FollowUpTaskPrompt, { TFollowUpValues } from "./FollowUpTaskPrompt";

/**
 * The single app-wide mount of FollowUpTaskPrompt (in DashboardLayout).
 * Completion sites open it through the followUpPrompt store; creating the
 * follow-up inherits the completed task's priority and assignee, and bumps
 * the shared tasks refresh version so every mounted task list picks it up.
 */
export default function FollowUpTaskPromptHost() {
  const source = useFollowUpPromptStore((s) => s.source);
  const close = useFollowUpPromptStore((s) => s.close);
  const bumpRefresh = useTasksRefreshStore((s) => s.bump);
  const { enqueueSnackbar } = useSnackbar();

  const handleCreate = async ({ title, dueAt, note }: TFollowUpValues) => {
    if (!source) return;
    try {
      await createLeadTask(source.leadId, {
        task: title,
        priority: source.priority,
        assignedToId: source.assignedToId,
        taskDueDate: dueAt,
        reminder: 0,
        description: note ? plainTextToHtml(note) : undefined,
      });
      close();
      enqueueSnackbar("Follow-up task created", { variant: "success" });
      bumpRefresh();
    } catch (error) {
      enqueueSnackbar(getErrorFromBackend(error), { variant: "error" });
    }
  };

  return (
    <FollowUpTaskPrompt
      open={!!source}
      saving={source?.saving ?? false}
      leadName={source?.leadName ?? ""}
      onClose={close}
      onCreate={handleCreate}
    />
  );
}
